// src/features/pages/admin/LeavePoliciesPage.tsx
// Phase 3: company-admin Leave Policies (writable for HR). List + tune existing
// policies (entitlement, carry-forward, notice) + delete. Live against
// leave-service; writes gated by leavepolicy:write. Creating policies needs a
// leave-type picker (no list endpoint yet) and is deferred.
import { listLegalEntities, type LegalEntity } from '@/services/payrollSettingsService';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useConfirm } from '@/features/shared/components/confirm/ConfirmProvider';
import { nudgeDana } from '@/features/shared/dana/danaNudge';
import toast from 'react-hot-toast';
import { CalendarDays, Loader2, Pencil, Plus, RotateCcw, Sparkles, Trash2, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import {
  listLeavePolicies, createLeavePolicy, updateLeavePolicy, deleteLeavePolicy,
  listDeletedLeavePolicies, restoreLeavePolicy,
  getLeaveTypes, initializeLeaveSetup, type LeavePolicy, type LeaveType,
} from '@/lib/hrApi';
import { getApiErrorMessage } from '@/lib/apiError';
import { useHasPermission } from '@/rbac/useRbac';
import { RequirePermission } from '@/rbac/RequirePermission';

const ACCRUAL_OPTIONS = ['annual', 'monthly', 'quarterly'];

// How this type is tracked. The same type_code behaves differently from one
// company to the next — one grants a fixed 52 work-from-home days, another
// accrues them, a third only wants them on the record — so it is a tenant
// setting, not a property of the code.
const TRACKING_OPTIONS = [
  {
    value: 'entitlement',
    label: 'Fixed allowance',
    hint: 'Granted in full at the start of the year. Requests are refused once it is used up.',
  },
  {
    value: 'accrual',
    label: 'Accrues over time',
    hint: 'Earned at the frequency below. Requests are refused beyond what has been earned so far.',
  },
  {
    value: 'informational',
    label: 'For the record only',
    hint: 'Logged and reported, never refused for balance. Use this when the employee is still working.',
  },
] as const;

/** Per-type delegation mode (leave-service LeaveType.delegation_mode). */
const DELEGATION_MODE_OPTIONS = [
  { value: 'policy', label: 'Follow the company delegation policy', hint: 'If the company requires delegation, a delegate must be named (or an existing delegation chosen) when requesting. They don\'t need to have accepted yet.' },
  { value: 'optional', label: 'Optional — submit immediately', hint: 'Never required. Typical for sick leave: the employee can report it straight away and delegate if they want to.' },
  { value: 'manager', label: 'Manager arranges cover', hint: 'Never required from the employee (e.g. emergencies). The request shows "no cover assigned yet" so the manager can arrange it.' },
  { value: 'exempt', label: 'No cover needed (still working)', hint: 'For time when the employee is still working, like Work From Home. They can still choose to delegate.' },
] as const;

// No tracking / delegation here: those are the leave TYPE's settings, and a
// default sent with a new policy overwrites the type (QA L7). They come only
// from the type itself (typeSettingsFor) or from the admin choosing them.
const blankPolicy = (): Partial<LeavePolicy> => ({
  leave_type_id: '',
  name: '',
  annual_entitlement: 0,
  accrual_frequency: 'annual',
  carry_forward_allowed: false,
  max_carry_forward: 0,
  notice_required_days: 0,
  evidence_required_after_days: null,
  is_active: true,
});

const LeavePoliciesInner: React.FC = () => {
  const { t } = useTranslation('admin');
  const confirm = useConfirm();
  const canWrite = useHasPermission('leavepolicy:write');
  const [policies, setPolicies] = useState<LeavePolicy[]>([]);
  // Deleted policies are kept out of the main list but must be visible
  // somewhere, or a delete cannot be undone without database access.
  const [deleted, setDeleted] = useState<LeavePolicy[]>([]);
  const [restoring, setRestoring] = useState<string | null>(null);
  const [types, setTypes] = useState<LeaveType[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);
  // `form` drives both create and edit. `mode` distinguishes them (create needs a
  // leave-type picker; edit keeps the existing id).
  const [form, setForm] = useState<Partial<LeavePolicy> | null>(null);
  // Multi-entity G5: a policy can be one company's own (it then governs the
  // leave of that company's employments). Offered only when the company list
  // is readable and there is more than one company.
  const [companies, setCompanies] = useState<LegalEntity[]>([]);
  useEffect(() => {
    listLegalEntities().then((l) => setCompanies(Array.isArray(l) ? l : [])).catch(() => setCompanies([]));
  }, []);
  const companyName = (id?: string | null) => companies.find((c) => c.id === id)?.name ?? '';
  const [mode, setMode] = useState<'create' | 'edit'>('edit');
  const lastInactiveReadFailed = useRef(false);

  // Returns the freshly read policies — active AND inactive — or null when the
  // read failed. A save is only confirmed from what this read shows (L7); the
  // list endpoint returns ACTIVE policies only, so a policy just deactivated is
  // found among the inactive ones (DEF-0mvnbe0a6005). `quiet`: the caller
  // reports a failed read itself (a save says "could not be confirmed"), so
  // load() doesn't ALSO toast — one failed read, one error (DEF-0mvnbe0ag009).
  const load = async ({ quiet = false }: { quiet?: boolean } = {}): Promise<LeavePolicy[] | null> => {
    setLoading(true);
    try {
      let inactiveFailed = false;
      const [pol, typ, del] = await Promise.all([
        listLeavePolicies(),
        getLeaveTypes().catch(() => [] as LeaveType[]),
        // Never let this fail the page: the deleted list is a recovery aid,
        // not something the admin is here for. (A save confirmation that needs
        // it treats a failed read as "could not confirm", below.)
        listDeletedLeavePolicies().catch(() => {
          inactiveFailed = true;
          return [] as LeavePolicy[];
        }),
      ]);
      const inactive = Array.isArray(del) ? del : [];
      setDeleted(inactive);
      const pols = Array.isArray(pol) ? pol : [];
      setPolicies(pols);
      if (pols.length === 0 && canWrite) nudgeDana({ key: 'leave_policies', message: 'No leave policies yet — want me to set up the standard ones?', question: 'Help me set up leave policies' });
      setTypes(Array.isArray(typ) ? typ : []);
      lastInactiveReadFailed.current = inactiveFailed;
      return [...pols, ...inactive.map((p) => ({ ...p, is_active: false }))];
    } catch (e) {
      if (!quiet) toast.error(getApiErrorMessage(e, t('leave_policies.load_error')));
      return null;
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    void load();
  }, []);

  const num = (v: string, fallback = 0) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
  };

  const openCreate = async () => {
    setMode('create');
    setForm(blankPolicy());
    // Ensure the type picker has options; seed defaults if the tenant has none.
    if (types.length === 0) {
      try {
        const typ = await getLeaveTypes();
        if (Array.isArray(typ)) setTypes(typ);
      } catch { /* picker just stays empty */ }
    }
  };

  // One-click: create the tenant's default leave types + standard policies.
  const seedDefaults = async () => {
    setSeeding(true);
    try {
      await initializeLeaveSetup();
      toast.success(t('leave_policies.seeded', { defaultValue: 'Default leave policies created.' }));
      await load();
    } catch (e) {
      toast.error(getApiErrorMessage(e, t('leave_policies.seed_error', { defaultValue: 'Could not create default policies.' })), { duration: 6000 });
    } finally {
      setSeeding(false);
    }
  };

  // ⚠️ Tracking and delegation mode belong to the leave TYPE, so every policy
  // of a type shows the same value and saving any one of them writes it.
  // A new policy therefore starts from the type's current settings — the
  // blank defaults would silently reset them (QA L7, 2026-10-24).
  const typeSettingsFor = (typeId: string): Partial<LeavePolicy> => {
    const sibling = policies.find((p) => p.leave_type_id === typeId);
    const lt = types.find((x) => x.id === typeId);
    const settings: Partial<LeavePolicy> = {};
    const category = sibling?.balance_category ?? lt?.balance_category;
    const delegation = sibling?.delegation_mode ?? lt?.delegation_mode;
    if (category) settings.balance_category = category;
    if (delegation) {
      settings.delegation_mode = delegation;
      settings.delegation_exempt = delegation === 'exempt';
    }
    return settings;
  };

  // The save took only if a FRESH read shows it: the policy row is there with
  // the values sent, and the type-level settings are the ones sent. The write
  // response is not enough — it echoes the request even when a later read
  // (another policy of the same type, a stale replica) says otherwise (L7).
  const notPersisted = (sent: Partial<LeavePolicy>, fresh?: Partial<LeavePolicy> | null) => {
    if (!fresh) return true;
    // Active / inactive is part of the save too: a deactivation the server did
    // not apply must not read as saved (and one it did must not read as failed).
    if (sent.is_active !== undefined && (sent.is_active !== false) !== (fresh.is_active !== false)) return true;
    const same = (a: unknown, b: unknown) => a === undefined || a === null || a === ''
      || (typeof a === 'number' || typeof b === 'number' ? Number(a) === Number(b) : a === b);
    // EVERY value the editor sends (DEF-0mvnbe0ad007), so a partly applied
    // save is never confirmed. Where blank is a value of its own — company
    // (blank = all companies) and evidence (blank = never) — blank must read
    // back as blank too, so those compare as sent, null for blank.
    const orNull = (v: unknown) => (v === undefined || v === null || v === '' ? null : v);
    const sameOrCleared = (key: 'legal_entity_id' | 'evidence_required_after_days') => {
      if (!(key in sent)) return true;
      const a = orNull(sent[key]);
      const b = orNull(fresh[key]);
      if (a === null || b === null) return a === b;
      return typeof a === 'number' || typeof b === 'number' ? Number(a) === Number(b) : a === b;
    };
    return !same(sent.name?.trim(), fresh.name?.trim())
      || !same(sent.leave_type_id, fresh.leave_type_id)
      || !same(sent.annual_entitlement, fresh.annual_entitlement)
      || !same(sent.accrual_frequency, fresh.accrual_frequency)
      || !same(sent.notice_required_days, fresh.notice_required_days)
      || !same(sent.carry_forward_allowed, fresh.carry_forward_allowed)
      // The cap only takes effect while carry-forward is allowed; a server
      // that normalises it when carry-forward is off is not a failed save.
      || (sent.carry_forward_allowed !== false && !same(sent.max_carry_forward, fresh.max_carry_forward))
      || !sameOrCleared('evidence_required_after_days')
      || !sameOrCleared('legal_entity_id')
      || !same(sent.delegation_mode, fresh.delegation_mode)
      || !same(sent.balance_category, fresh.balance_category);
  };

  const save = async () => {
    if (!form) return;
    if (!form.name?.trim()) {
      toast.error(t('common.name_required'));
      return;
    }
    if (mode === 'create' && !form.leave_type_id) {
      toast.error(t('leave_policies.type_required', { defaultValue: 'Choose a leave type.' }));
      return;
    }
    setSaving(true);
    try {
      const saved = (mode === 'create'
        ? await createLeavePolicy(form)
        : await updateLeavePolicy(form.id as string, form as LeavePolicy)) as LeavePolicy;
      // Reload every row, not just this one: the other policies of the same
      // type carry the same type-level settings, and a stale copy re-opened
      // and saved would quietly write the old value back.
      const fresh = await load({ quiet: true });
      if (fresh === null) {
        // The read failed: the one error is "could not be confirmed" — never
        // "updated", and never a second "could not load" on top of it.
        toast.error(t('leave_policies.not_confirmed', {
          defaultValue: 'The change was sent, but it could not be confirmed. Reload the page to check what is in force.',
        }), { duration: 8000 });
        return;
      }
      const freshRow = fresh.find((p) => p.id === saved?.id);
      if (!freshRow && form.is_active === false && lastInactiveReadFailed.current) {
        // Deactivated, but the inactive list could not be read: unconfirmed,
        // not "did not save".
        toast.error(t('leave_policies.not_confirmed', {
          defaultValue: 'The change was sent, but it could not be confirmed. Reload the page to check what is in force.',
        }), { duration: 8000 });
        return;
      }
      if (notPersisted(form, freshRow)) {
        toast.error(t('leave_policies.not_persisted', {
          defaultValue: 'The change did not save. The values shown are the ones in force — please try again.',
        }), { duration: 8000 });
        return;
      }
      toast.success(mode === 'create'
        ? t('leave_policies.created', { defaultValue: 'Leave policy created.' })
        : t('leave_policies.updated'));
      setForm(null);
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, mode === 'create' ? t('leave_policies.create_error', { defaultValue: 'Could not create policy.' }) : t('leave_policies.update_error')));
    } finally {
      setSaving(false);
    }
  };

  const restore = async (p: LeavePolicy) => {
    setRestoring(p.id);
    try {
      await restoreLeavePolicy(p.id);
      toast.success(t('leave_policies.restored', {
        defaultValue: '{{name}} restored', name: p.name,
      }));
      // Reload rather than moving the row by hand: the server decides, and a
      // 409 means nothing moved.
      await load();
    } catch (e) {
      toast.error(getApiErrorMessage(e, t('leave_policies.restore_error', {
        defaultValue: 'Could not restore this policy.',
      })), { duration: 8000 });
    } finally {
      setRestoring(null);
    }
  };

  const remove = async (p: LeavePolicy) => {
    if (!(await confirm({ title: 'Delete leave policy', message: t('leave_policies.delete_confirm', { name: p.name }), confirmLabel: 'Delete', danger: true }))) return;
    try {
      await deleteLeavePolicy(p.id);
      setPolicies((prev) => prev.filter((x) => x.id !== p.id));
      // It moves to the deleted list rather than vanishing, so the admin can
      // see where it went and undo it.
      setDeleted((prev) => [{ ...p, is_active: false }, ...prev]);
      toast.success(t('leave_policies.deleted'));
    } catch (e) {
      toast.error(getApiErrorMessage(e, t('leave_policies.delete_error')), { duration: 6000 });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1">
          <h1 className="text-h2 text-charcoal">{t('leave_policies.title')}</h1>
          <p className="text-muted-foreground mt-1">{t('leave_policies.subtitle')}</p>
        </div>
        {canWrite && !form && (
          <Button className="bg-orange-500 hover:bg-orange-600 text-white" onClick={openCreate}>
            <Plus className="w-4 h-4 mr-1" /> {t('leave_policies.new', { defaultValue: 'New Policy' })}
          </Button>
        )}
      </div>

      {form && (
        <Card className="border-orange-200 dark:border-orange-500/30">
          <CardHeader className="rounded-t-xl flex-row items-center">
            <CardTitle className="flex-1 text-orange-700">
              {mode === 'create'
                ? t('leave_policies.new_title', { defaultValue: 'New leave policy' })
                : t('leave_policies.edit', { name: form.type_name || form.name })}
            </CardTitle>
            <button type="button" onClick={() => setForm(null)} aria-label={t('common.close')} className="text-muted-foreground hover:text-foreground">
              <X className="w-4 h-4" />
            </button>
          </CardHeader>
          <CardContent className="pt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {mode === 'create' && (
              <Field label={t('leave_policies.leave_type', { defaultValue: 'Leave type' })}>
                <select value={form.leave_type_id || ''} onChange={(e) => {
                  // Drop the previous type's settings: only what THIS type reports
                  // is carried, and anything unknown is left out of the request.
                  const { balance_category: _c, delegation_mode: _m, delegation_exempt: _x, ...rest } = form;
                  setForm({ ...rest, ...typeSettingsFor(e.target.value), leave_type_id: e.target.value });
                }} className={inputCls}>
                  <option value="">{t('leave_policies.choose_type', { defaultValue: 'Choose a leave type…' })}</option>
                  {types.map((lt) => <option key={lt.id} value={lt.id}>{lt.name}{lt.type_code ? ` (${lt.type_code})` : ''}</option>)}
                </select>
              </Field>
            )}
            {companies.length > 1 && (
              <Field label={t('leave_policies.company', { defaultValue: 'Company' })}>
                <select data-testid="policy-company" value={form.legal_entity_id ?? ''}
                  onChange={(e) => setForm({ ...form, legal_entity_id: e.target.value || null })} className={inputCls}>
                  <option value="">{t('leave_policies.all_companies', { defaultValue: 'All companies (default)' })}</option>
                  {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <p className="mt-1 text-xs text-muted-foreground">
                  {t('leave_policies.company_hint', { defaultValue: "A company's own policy applies to the people it employs; everyone else keeps the default." })}
                </p>
              </Field>
            )}
            <Field label={t('leave_policies.policy_name')}>
              <input value={form.name || ''} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
            </Field>
            <Field label={t('leave_policies.annual_entitlement')}>
              <input type="number" value={form.annual_entitlement ?? 0} onChange={(e) => setForm({ ...form, annual_entitlement: num(e.target.value) })} className={inputCls} />
            </Field>
            <Field label={t('leave_policies.tracking', { defaultValue: 'How is this tracked?' })}>
              <select value={(form.balance_category as string) || 'accrual'}
                      onChange={(e) => setForm({ ...form, balance_category: e.target.value as LeavePolicy['balance_category'] })}
                      className={inputCls} data-testid="tracking-mode">
                {TRACKING_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {t(`leave_policies.tracking_${o.value}`, { defaultValue: o.label })}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-muted-foreground">
                {t(`leave_policies.tracking_${form.balance_category || 'accrual'}_hint`, {
                  defaultValue: TRACKING_OPTIONS.find((o) => o.value === (form.balance_category || 'accrual'))?.hint ?? '',
                })}
              </p>
            </Field>
            {/* Frequency only means something for an accrued type: a fixed
                allowance is granted up front and an informational type is never
                deducted, so offering the choice there would imply an effect it
                does not have. */}
            {(form.balance_category || 'accrual') === 'accrual' && (
              <Field label={t('leave_policies.accrual', { defaultValue: 'Accrual frequency' })}>
                <select value={form.accrual_frequency || 'annual'} onChange={(e) => setForm({ ...form, accrual_frequency: e.target.value })} className={inputCls}>
                  {ACCRUAL_OPTIONS.map((a) => <option key={a} value={a}>{t(`leave_policies.accrual_${a}`, { defaultValue: a.charAt(0).toUpperCase() + a.slice(1) })}</option>)}
                </select>
              </Field>
            )}
            <Field label={t('leave_policies.notice_required')}>
              <input type="number" value={form.notice_required_days ?? 0} onChange={(e) => setForm({ ...form, notice_required_days: num(e.target.value) })} className={inputCls} />
            </Field>
            <Field label={t('leave_policies.max_carry_forward')}>
              <input type="number" value={form.max_carry_forward ?? 0} onChange={(e) => setForm({ ...form, max_carry_forward: num(e.target.value) })} className={inputCls} />
            </Field>
            <Field label={t('leave_policies.evidence_after_days', { defaultValue: 'Supporting document required beyond (days)' })}>
              <input type="number" min={0} placeholder={t('leave_policies.evidence_never', { defaultValue: 'blank = never' }) as string}
                value={form.evidence_required_after_days ?? ''}
                onChange={(e) => setForm({ ...form, evidence_required_after_days: e.target.value === '' ? null : num(e.target.value) })}
                className={inputCls} />
            </Field>
            <label className="flex items-center gap-2 text-sm text-foreground">
              <input type="checkbox" checked={!!form.carry_forward_allowed} onChange={(e) => setForm({ ...form, carry_forward_allowed: e.target.checked })} />
              {t('leave_policies.allow_carry_forward')}
            </label>
            <label className="flex items-center gap-2 text-sm text-foreground">
              <input type="checkbox" checked={form.is_active ?? true} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
              {t('leave_policies.active')}
            </label>
            {/* Cover is a separate question from balance, decided per TYPE
                (owner decision 2026-10-22; extends the old "no delegation
                needed" switch). Acceptance by the delegate never blocks
                submitting or approving — see the delegation policy. */}
            <div className="sm:col-span-2">
              <Field label={t('leave_policies.delegation_mode', { defaultValue: 'Cover / delegation for this type' })}>
                <select value={(form.delegation_mode as string) || (form.delegation_exempt ? 'exempt' : 'policy')}
                        onChange={(e) => {
                          const v = e.target.value as NonNullable<LeavePolicy['delegation_mode']>;
                          setForm({ ...form, delegation_mode: v, delegation_exempt: v === 'exempt' });
                        }}
                        className={inputCls} data-testid="delegation-mode">
                  {DELEGATION_MODE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {t(`leave_policies.delegation_mode_${o.value}`, { defaultValue: o.label })}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-muted-foreground">
                  {t(`leave_policies.delegation_mode_${(form.delegation_mode as string) || (form.delegation_exempt ? 'exempt' : 'policy')}_hint`, {
                    defaultValue: DELEGATION_MODE_OPTIONS.find((o) => o.value === ((form.delegation_mode as string) || (form.delegation_exempt ? 'exempt' : 'policy')))?.hint ?? '',
                  })}
                </p>
              </Field>
            </div>
            <div className="sm:col-span-2 flex items-center gap-2">
              <Button className="bg-orange-500 hover:bg-orange-600 text-white" onClick={save} disabled={saving}>
                {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} {mode === 'create' ? t('leave_policies.create', { defaultValue: 'Create policy' }) : t('common.save_changes')}
              </Button>
              <Button variant="outline" onClick={() => setForm(null)} disabled={saving}>{t('common.cancel')}</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="border-orange-100 dark:border-orange-500/20">
        <CardHeader className="rounded-t-xl">
          <CardTitle className="flex items-center gap-2 text-orange-700">
            <CalendarDays className="w-5 h-5" /> {t('leave_policies.all')}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          {loading ? (
            <div className="py-12 flex items-center justify-center text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> {t('common.loading')}
            </div>
          ) : policies.length === 0 ? (
            <div className="py-12 flex flex-col items-center justify-center gap-4 text-center">
              <p className="text-muted-foreground">{t('leave_policies.empty')}</p>
              {canWrite && (
                <div className="flex items-center gap-2 flex-wrap justify-center">
                  <Button className="bg-orange-500 hover:bg-orange-600 text-white" onClick={seedDefaults} disabled={seeding}>
                    {seeding ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
                    {t('leave_policies.seed_defaults', { defaultValue: 'Create default policies' })}
                  </Button>
                  <Button variant="outline" onClick={openCreate}>
                    <Plus className="w-4 h-4 mr-1" /> {t('leave_policies.new', { defaultValue: 'New Policy' })}
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {policies.map((p) => (
                <li key={p.id} className="py-3 flex items-center gap-3 flex-wrap">
                  <div className="flex-1 min-w-[180px]">
                    <div className="font-medium text-foreground flex items-center gap-2">
                      {p.name}
                      {p.legal_entity_id && companyName(p.legal_entity_id) && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground">{companyName(p.legal_entity_id)}</span>
                      )}
                      {p.type_code && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-orange-100 dark:bg-orange-500/[0.12] text-orange-700 dark:text-orange-300">{p.type_code}</span>
                      )}
                      {p.is_active === false && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground">{t('common.inactive')}</span>
                      )}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {t('leave_policies.days_per_year', { count: p.annual_entitlement })}
                      {p.carry_forward_allowed ? t('leave_policies.carry_forward_up_to', { count: p.max_carry_forward ?? 0 }) : t('leave_policies.no_carry_forward')}
                      {p.notice_required_days ? t('leave_policies.notice_days', { count: p.notice_required_days }) : ''}
                    </div>
                  </div>
                  {canWrite && (
                    <div className="flex items-center gap-1">
                      <button type="button" onClick={() => { setMode('edit'); setForm(p); }} aria-label={t('common.edit', { name: p.name })}
                        className="p-1.5 rounded-[8px] text-muted-foreground hover:text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-500/[0.12]">
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button type="button" onClick={() => remove(p)} aria-label={t('common.delete', { name: p.name })}
                        className="p-1.5 rounded-[8px] text-muted-foreground hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-500/[0.12]">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* ⚠️ Deleted policies, and the way back.
          Deleting is a SOFT delete, so nothing is ever lost — but without this
          section an admin could not see the deleted policy to restore it, and
          the old unique constraint stopped them re-creating it under the same
          name either. A misclick then needed SQL against production (Fern,
          2026-10-12), while every request of that type failed meanwhile.
          Hidden entirely when there is nothing deleted, so it costs no
          attention in the normal case. */}
      {!loading && deleted.length > 0 && (
        <Card className="border-border">
          <CardHeader className="rounded-t-xl">
            <CardTitle className="flex items-center gap-2 text-muted-foreground text-base">
              <Trash2 className="w-4 h-4" />
              {t('leave_policies.deleted_title', {
                defaultValue: 'Deleted policies ({{count}})', count: deleted.length,
              })}
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              {t('leave_policies.deleted_help', {
                defaultValue:
                  'These are kept, not erased. Employees cannot request this leave type while its policy is deleted. Restore brings the policy back with its original settings.',
              })}
            </p>
          </CardHeader>
          <CardContent className="pt-4">
            <ul className="divide-y divide-border">
              {deleted.map((p) => (
                <li key={p.id} className="py-3 flex items-center gap-3 flex-wrap">
                  <div className="flex-1 min-w-[180px]">
                    <div className="font-medium text-muted-foreground flex items-center gap-2">
                      {p.name}
                      {p.type_code && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground">{p.type_code}</span>
                      )}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {t('leave_policies.days_per_year', { count: p.annual_entitlement })}
                    </div>
                  </div>
                  {canWrite && (
                    <Button variant="outline" size="sm" onClick={() => restore(p)} disabled={restoring === p.id}>
                      {restoring === p.id
                        ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                        : <RotateCcw className="w-4 h-4 mr-1" />}
                      {t('leave_policies.restore', { defaultValue: 'Restore' })}
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const inputCls =
  'mt-1 w-full rounded-[10px] border border-border bg-cream px-3 py-2 text-sm text-foreground outline-none focus:border-orange-400';

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <label className="text-sm font-medium text-foreground">{label}</label>
    {children}
  </div>
);

export const LeavePoliciesPage: React.FC = () => (
  // Managing leave policies requires write; read-only roles are blocked from the
  // page, not just hidden from the nav.
  <RequirePermission perm="leavepolicy:write">
    <LeavePoliciesInner />
  </RequirePermission>
);

export default LeavePoliciesPage;
